import { L } from '../components/providers/Lokalise'
import { Member } from '../models/Member'
import { Profile } from '../models/Profile'
import { colors } from '../theme/palette'

export const getAge = (birthYear: number): number => {
    const currentDate = new Date()
    const currentYear = currentDate.getFullYear()
    return currentYear - birthYear
}

export const uniq = (items: Member[], key: string): Member[] => {
    const arrayUniqueByKey = [
        ...new Map(items.map((item) => [item[key], item])).values(),
    ]

    return arrayUniqueByKey
}

export const getUpdateTypeLabel = (type: string): string => {
    let label = type

    switch (type) {
        case 'OpdaterMedlem':
            label = L('MINEOPDATERINGER_TYPE_OpdaterMedlem') // 'Redigér medlemsdata'
            break
        case 'MedlemOphoert':
            label = L('MINEOPDATERINGER_TYPE_MedlemOphoert') // 'Stoppet på arbejdsstedet'
            break
        case 'TilfoejMedlemArbejdssted':
            label = L('MINEOPDATERINGER_TYPE_TilfoejMedlemArbejdssted') // 'Tilføj nyt arbejdssted på medlemmet'
            break
        case 'OpdaterOgTilfoejEksisterendeMedlem':
            label = L(
                'MINEOPDATERINGER_TYPE_OpdaterOgTilfoejEksisterendeMedlem'
            ) // 'Tilføj kollega til Mine medlemmer'
            break
        case 'SkiftMedlemArbejdssted':
            label = L('MINEOPDATERINGER_TYPE_SkiftMedlemArbejdssted') // 'Fjern medlem fra arbejdsstedet'
            break
        case 'MedlemSkiftetStillingTRIkkeDaekker':
            label = L(
                'MINEOPDATERINGER_TYPE_MedlemSkiftetStillingTRIkkeDaekker'
            )
            break
        default:
            break
    }

    return label
}

export const getUpdateStatusLabel = (status: string): string => {
    let label = status

    switch (status) {
        case 'FaerdigBehandlet':
            label = L('MINEOPDATERINGER_STATUS_FaerdigBehandlet') // 'Færdig behandlet'
            break
        case 'UnderOpdatering':
            label = L('MINEOPDATERINGER_STATUS_UnderOpdatering') // Afventer opdatering'
            break
        default:
            break
    }

    return label
}

export const getCaseStatusLabel = (type: string): string => {
    let label = type

    switch (type) {
        case 'FristOverskredet':
            label = 'Frist overskredet'
            break
        case 'Igang':
            label = 'Sag i gang'
            break
        case 'AfventerReminder':
            label = 'Afventer reminder'
            break
        default:
            break
    }

    return label
}

export const getCaseStatusColorCode = (
    type: string
): {
    color: string
    bgColor: string
} => {
    let colorCode = {
        color: '',
        bgColor: '',
    }

    switch (type) {
        case 'FristOverskredet':
            colorCode = {
                color: '#910012',
                bgColor: '#d9001b1a',
            }
            break
        case 'Igang':
            colorCode = {
                color: '#297A71',
                bgColor: '#297a711a',
            }
            break
        case 'AfventerReminder':
            colorCode = {
                color: '#F1C400',
                bgColor: '#FEF9E5',
            }
            break
        case 'Afsluttet':
            colorCode = {
                color: '#555555',
                bgColor: '#f2f2f2',
            }
            break
        default:
            break
    }
    return colorCode
}

// 02	Fællestillidssuppleant
// 04	Tillidssuppleant
// 08	MED Hoved.udv.suppleant
// 16	Udgået Lokal SU-supplean
// 18	Centralt hoved SU-suppl.
// 24	Distrikttillidsrep.suppl.
// 31	Arbejdsmiljøudvalgsuppl.
// 36	Suppl.midlert.HSU(MT HSU)
// 37	HSIU - suppleant
export const isSuppleantFor = (type: string): boolean => {
    const types: string[] = [
        '02',
        '04',
        '08',
        '16',
        '18',
        '24',
        '31',
        '36',
        '37',
    ]

    return types.indexOf(type) !== -1
}

export const getSuppleantNamesForTR = (
    type: string,
    profile: Profile
): string => {
    const names = []

    profile.trForOthers.forEach((tfo) => {
        tfo.typeOfRepresentatives.forEach((tor) => {
            if (tor.supplementedOfDutyType === type) {
                names.push(tfo.fullName)
            }
        })
    })

    return names.join(', ')
}

/**
 * Detects if the current browser is Safari on a mobile device
 * @returns {boolean} True if Safari on mobile, false otherwise
 */
export const isSafariMobile = (): boolean => {
    const { userAgent } = navigator
    const isMobile = /iPhone|iPad|iPod|Android/i.test(userAgent)
    const isSafari = /Safari/i.test(userAgent) && !/Chrome/i.test(userAgent)

    return isMobile && isSafari
}

/**
 * Checks if the current environment is test (not local development, not production)
 * @returns {boolean} True if running in test environment, false otherwise
 */
export const isTestEnvironment = (): boolean => {
    const isTest =
        process.env.NODE_ENV === 'test' ||
        process.env.REACT_APP_ENVIRONMENT === 'test' ||
        window.location.hostname.includes('test') ||
        window.location.hostname.includes('staging')
    return isTest
}

/**
 * Checks if a username is a dummy user based on specific criteria
 * @param upn - The user principal name (email address)
 * @returns {boolean} True if the user is a dummy user, false otherwise
 */
export const isDummyUser = (upn: string): boolean => {
    if (!upn) return false

    const username = upn.split('@')[0]

    const isTest = isTestEnvironment()

    return (
        (isTest && upn === '1902pf@foa2test.dk') ||
        (username.endsWith('zz') &&
            username.length >= 2 &&
            parseInt(username.substring(0, 2), 10) >= 50)
    )
}
